// Pestaña Etiquetar: buscar o elegir el producto (tipo, grupo, producto),
// revisar la ficha con la vista previa e imprimir la etiqueta. Si el producto
// ya tiene una etiqueta abierta, se pregunta antes si se ha terminado.
import React, { useCallback, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChefHat, Loader2, Package, Printer, RotateCcw, Search, Undo2, X,
} from 'lucide-react';
import { etiquetasAPI, textoErrorApi } from '../../api/api';
import { toast } from '../../hooks/use-toast';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import VistaEtiqueta from './VistaEtiqueta';
import { FuenteProducto } from './ProductosTab';
import { AVISO_SIMULACION } from '../../context/VistaEmpleadoContext';
import { hoyMadrid } from '../../utils/fechas';
import { prepararImpresion, imprimirEtiqueta, cancelarImpresion } from '../../utils/imprimirEtiqueta';
import {
  AVISO_SUPERA_APPCC, NOMBRE_CONSERVACION, TEXTO_HASTA_ENVASE, caducidadPrevista, claveProducto, coincideBusqueda,
  diaMes, esHastaEnvase, fechaCorta, horaDe, inicioApertura, textoAbiertas, textoVida, vistaDeApertura, vistaPrevia,
} from '../../utils/etiquetas';

const MAX_RESULTADOS = 80;

const claseTarjeta = 'bg-white rounded-xl border border-gray-200';
const claseBotonPrincipal = 'w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-duetto-primary text-white text-base font-semibold hover:bg-duetto-primary/90 disabled:opacity-50 disabled:pointer-events-none';
const claseBotonSecundario = 'h-12 inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none';
const claseTesela = 'bg-white rounded-xl border border-gray-200 p-3 text-left hover:border-duetto-primary active:bg-gray-50 transition min-h-[72px] flex flex-col justify-between';

const CLASE_CONSERVACION = {
  congelado: 'bg-gray-900 text-white border-gray-900',
  refrigerado: 'bg-sky-50 text-sky-800 border-sky-300',
  seco: 'bg-stone-100 text-stone-700 border-stone-200',
};

const ACCIONES = {
  ingrediente: [
    { valor: 'abrir', texto: 'Abrir' },
    { valor: 'congelar', texto: 'Congelar' },
  ],
  plato: [
    { valor: 'elaborar', texto: 'Elaborar' },
    { valor: 'congelar', texto: 'Elaborar y congelar' },
  ],
};

const nombreOrden = (a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es');

// '12/10 a las 09:30' o '12/10'
const fechaLegible = (iso) => {
  const h = horaDe(iso);
  return h ? `${diaMes(iso)} a las ${h}` : diaMes(iso);
};

const caducaLegible = (apertura) => (
  apertura?.caduca_con_hora ? fechaLegible(apertura.caduca) : diaMes(apertura?.caduca)
);

const Chip = ({ className = '', children }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${className}`}>
    {children}
  </span>
);

const errorDe = (e, porDefecto) => textoErrorApi(e?.response?.data?.detail, porDefecto);

// ==================== FICHA DEL PRODUCTO ====================

const FichaProducto = ({ producto, abiertas, onTerminar, onRecargar, soloLectura }) => {
  const esPlato = producto.tipo === 'plato';
  const [accion, setAccion] = useState(esPlato ? 'elaborar' : 'abrir');
  const [caducidadEnvase, setCaducidadEnvase] = useState('');
  const [loteFabricante, setLoteFabricante] = useState('');
  const [trasvasado, setTrasvasado] = useState(false);
  const [usarAbierta, setUsarAbierta] = useState(false);
  const [marcadas, setMarcadas] = useState([]);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [reimprimiendo, setReimprimiendo] = useState(false);
  const [deshaciendo, setDeshaciendo] = useState(false);
  const [hecha, setHecha] = useState(null);

  const hoy = hoyMadrid();
  const envaseCaducado = !esPlato && !!caducidadEnvase && caducidadEnvase < hoy;
  const modoLista = !!producto.varias_abiertas || abiertas.length > 1;
  const unaAbierta = !modoLista && abiertas.length === 1 ? abiertas[0] : null;
  const unaCaducada = unaAbierta?.estado_caducidad === 'caducado';

  const opciones = { caducidadEnvase: esPlato ? '' : caducidadEnvase, loteFabricante: esPlato ? '' : loteFabricante, trasvasado };
  const datosVista = vistaPrevia(producto, accion, opciones);
  const prevista = caducidadPrevista(producto, accion, { caducidadEnvase: esPlato ? '' : caducidadEnvase });
  // "Hasta la fecha del envase": al abrir, la caducidad del envase es obligatoria
  const envaseObligatorio = accion === 'abrir' && esHastaEnvase(producto);
  const faltaEnvase = !!prevista.faltaEnvase;
  const bloqueado = envaseCaducado || faltaEnvase;

  // Crea la etiqueta y la imprime. `cerrarIds` pasan a gastadas/consumidas
  // (las cierra el servidor); `retirarIds` (caducadas) se retiran antes.
  const imprimir = useCallback(async ({ cerrarIds = [], retirarIds = [] } = {}) => {
    if (soloLectura) {
      toast({ title: AVISO_SIMULACION });
      return;
    }
    if (envaseCaducado) {
      toast({ title: 'El envase está caducado: no se puede etiquetar.', variant: 'destructive' });
      return;
    }
    if (faltaEnvase) {
      toast({ title: 'Este producto se consume hasta la fecha del envase: indica su caducidad.', variant: 'destructive' });
      return;
    }
    // En el mismo clic, antes de esperar al servidor (iOS)
    const ventana = prepararImpresion();
    setImprimiendo(true);
    try {
      if (retirarIds.length) {
        await Promise.all(retirarIds.map((id) => etiquetasAPI.cambiarEstado(id, 'retirada')));
      }
      const body = {
        tipo: producto.tipo,
        producto_id: producto.id,
        accion,
        trasvasado: accion === 'abrir' && trasvasado,
        cerrar_ids: cerrarIds,
      };
      if (!esPlato) {
        if (caducidadEnvase) body.caducidad_envase = caducidadEnvase;
        if (loteFabricante.trim()) body.lote_fabricante = loteFabricante.trim();
      }
      const { data: apertura } = await etiquetasAPI.crear(body);
      // El servidor ignora las que no son del producto: se reabren solo las que cerró
      const cerradas = Array.isArray(apertura?.cerradas_ids) ? apertura.cerradas_ids : cerrarIds;
      setHecha({ apertura, reabrir: [...cerradas, ...retirarIds] });
      onRecargar();
      try {
        await imprimirEtiqueta(apertura, ventana);
      } catch (e) {
        toast({
          title: 'No se pudo abrir la impresión',
          description: 'La etiqueta está creada: pulsa Imprimir otra vez.',
          variant: 'destructive',
        });
      }
    } catch (e) {
      cancelarImpresion(ventana);
      if (retirarIds.length) onRecargar();
      toast({ title: 'No se pudo crear la etiqueta', description: errorDe(e, 'Inténtalo otra vez.'), variant: 'destructive' });
    } finally {
      setImprimiendo(false);
    }
  }, [soloLectura, envaseCaducado, faltaEnvase, producto.tipo, producto.id, accion, trasvasado, esPlato, caducidadEnvase, loteFabricante, onRecargar]);

  const imprimirConMarcadas = useCallback(() => {
    const porId = new Map(abiertas.map((a) => [a.id, a]));
    const cerrarIds = [];
    const retirarIds = [];
    marcadas.forEach((id) => {
      const a = porId.get(id);
      if (!a) return;
      if (a.estado_caducidad === 'caducado') retirarIds.push(id);
      else cerrarIds.push(id);
    });
    imprimir({ cerrarIds, retirarIds });
  }, [abiertas, marcadas, imprimir]);

  const reimprimir = useCallback(async () => {
    if (!hecha) return;
    const ventana = prepararImpresion();
    setReimprimiendo(true);
    try {
      await imprimirEtiqueta(hecha.apertura, ventana);
    } catch (e) {
      cancelarImpresion(ventana);
      toast({ title: 'No se pudo imprimir', description: 'Inténtalo otra vez.', variant: 'destructive' });
    } finally {
      setReimprimiendo(false);
    }
  }, [hecha]);

  const deshacer = useCallback(async () => {
    if (!hecha) return;
    if (soloLectura) {
      toast({ title: AVISO_SIMULACION });
      return;
    }
    setDeshaciendo(true);
    try {
      await etiquetasAPI.cambiarEstado(hecha.apertura.id, 'anulada');
      if (hecha.reabrir.length) {
        await Promise.all(hecha.reabrir.map((id) => etiquetasAPI.cambiarEstado(id, 'abierta')));
      }
      toast({
        title: 'Etiqueta anulada',
        description: hecha.reabrir.length
          ? 'Tira la etiqueta impresa. Las que habías cerrado vuelven a estar abiertas.'
          : 'Tira la etiqueta impresa.',
      });
      setHecha(null);
      setUsarAbierta(false);
      setMarcadas([]);
      onRecargar();
    } catch (e) {
      toast({ title: 'No se pudo deshacer', description: errorDe(e, 'Inténtalo otra vez.'), variant: 'destructive' });
    } finally {
      setDeshaciendo(false);
    }
  }, [hecha, soloLectura, onRecargar]);

  const alternarMarcada = useCallback((id, marcada) => {
    setMarcadas((prev) => (marcada ? [...prev.filter((x) => x !== id), id] : prev.filter((x) => x !== id)));
  }, []);

  // ---------- Etiqueta hecha ----------
  if (hecha) {
    return (
      <div className="space-y-3" data-testid="etiqueta-hecha">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <div>
              <p className="text-base font-semibold text-emerald-900">
                Etiqueta lista. Lote <span className="font-mono">{hecha.apertura.lote}</span>
              </p>
              <p className="text-sm text-emerald-800 mt-0.5">
                {hecha.apertura.nombre} · consumir antes del {caducaLegible(hecha.apertura)}
                {hecha.apertura.caduca_por_envase ? ' (manda la fecha del envase)' : ''}
              </p>
            </div>
          </div>
        </div>
        <div className={`${claseTarjeta} p-3`}>
          <VistaEtiqueta datos={vistaDeApertura(hecha.apertura)} titulo="Etiqueta impresa" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button type="button" className={claseBotonSecundario} onClick={reimprimir} disabled={reimprimiendo} data-testid="etiqueta-reimprimir">
            {reimprimiendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
            Imprimir otra vez
          </button>
          <button
            type="button"
            className={`${claseBotonSecundario} text-red-700 border-red-200 hover:bg-red-50`}
            onClick={deshacer}
            disabled={deshaciendo}
            data-testid="etiqueta-deshacer"
          >
            {deshaciendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
            Deshacer: me he equivocado
          </button>
          <button type="button" className={claseBotonPrincipal} onClick={onTerminar} data-testid="etiqueta-terminar">
            Hecho
          </button>
        </div>
      </div>
    );
  }

  // ---------- Ficha ----------
  const conservacionChip = accion === 'congelar' ? 'congelado' : producto.conservacion;

  return (
    <div className="space-y-3" data-testid="etiqueta-ficha">
      <div className={`${claseTarjeta} p-4`}>
        <h2 className="text-lg font-semibold text-gray-900 leading-tight">{producto.nombre}</h2>
        <p className="text-xs text-gray-500 mt-0.5">{esPlato ? 'Plato' : 'Ingrediente'} · {producto.grupo}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          <Chip className={CLASE_CONSERVACION[conservacionChip] || CLASE_CONSERVACION.refrigerado}>
            {NOMBRE_CONSERVACION[conservacionChip] || conservacionChip} · {datosVista?.rango}
          </Chip>
          {esHastaEnvase(producto) ? (
            <Chip className="bg-white text-gray-700 border-gray-300">{TEXTO_HASTA_ENVASE}</Chip>
          ) : textoVida(producto) && (
            <Chip className="bg-white text-gray-700 border-gray-300">{`Dura ${textoVida(producto)}${esPlato ? '' : ' abierto'}`}</Chip>
          )}
          {(producto.alergenos || []).map((a) => (
            <Chip key={`c-${a}`} className="bg-red-50 text-red-700 border-red-200">{a}</Chip>
          ))}
          {(producto.trazas || []).map((a) => (
            <Chip key={`t-${a}`} className="bg-orange-50 text-orange-700 border-orange-200">Trazas: {a}</Chip>
          ))}
          {producto.sin_datos_alergenos && (
            <Chip className="bg-gray-100 text-gray-700 border-gray-300">Sin datos de alérgenos</Chip>
          )}
          {!producto.confirmado && (
            <Chip className="bg-amber-50 text-amber-800 border-amber-200">Vida útil sin confirmar por cocina</Chip>
          )}
        </div>
        <FuenteProducto producto={producto} className="mt-2" />
        {producto.supera_appcc && (
          <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {AVISO_SUPERA_APPCC}
          </p>
        )}
        {producto.sin_grupo && (
          <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Producto sin grupo: revisa la vida útil en Productos.
          </p>
        )}
        {!producto.se_etiqueta && (
          <p className="mt-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            No se suele etiquetar. Puedes imprimirla igualmente.
          </p>
        )}
      </div>

      {/* Acción */}
      <div className={`${claseTarjeta} p-3 space-y-3`}>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Qué vas a hacer">
          {ACCIONES[producto.tipo].map((op) => {
            const activa = accion === op.valor;
            return (
              <button
                key={op.valor}
                type="button"
                role="radio"
                aria-checked={activa}
                onClick={() => setAccion(op.valor)}
                className={`h-11 rounded-lg border text-sm font-semibold transition ${activa
                  ? 'bg-duetto-primary text-white border-duetto-primary'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                data-testid={`etiqueta-accion-${op.valor}`}
              >
                {op.texto}
              </button>
            );
          })}
        </div>

        {!esPlato && (
          <div className="space-y-3">
            <div className={`rounded-lg border p-3 ${faltaEnvase
              ? 'border-amber-300 bg-amber-50'
              : 'border-duetto-primary/40 bg-emerald-50/40'}`}
            >
              <label htmlFor="etq-caducidad-envase" className="block text-sm font-semibold text-gray-900">
                {envaseObligatorio ? 'Caducidad del envase (obligatoria)' : 'Caducidad del envase'}
              </label>
              <p className={`text-xs mb-1.5 ${faltaEnvase ? 'text-amber-900 font-medium' : 'text-gray-600'}`}>
                {envaseObligatorio
                  ? 'Este producto se consume hasta la fecha del envase.'
                  : 'Si la pones, la etiqueta nunca pasa de esa fecha.'}
              </p>
              <div className="flex gap-2">
                <Input
                  id="etq-caducidad-envase"
                  type="date"
                  value={caducidadEnvase}
                  onChange={(e) => setCaducidadEnvase(e.target.value)}
                  className="h-11 bg-white border-gray-300 text-base"
                  required={envaseObligatorio}
                  aria-required={envaseObligatorio}
                  data-testid="etiqueta-caducidad-envase"
                />
                {caducidadEnvase && (
                  <button
                    type="button"
                    onClick={() => setCaducidadEnvase('')}
                    className="h-11 w-11 flex-shrink-0 inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                    aria-label="Quitar la caducidad del envase"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {envaseCaducado && (
                <p className="mt-1.5 text-sm font-medium text-red-700">El envase está caducado: no se puede etiquetar.</p>
              )}
              {!envaseCaducado && prevista.porEnvase && (
                <p className="mt-1.5 text-sm text-gray-700">
                  {envaseObligatorio
                    ? `Caduca el ${fechaCorta(prevista.caduca)}, con el envase.`
                    : `Manda la fecha del envase: caduca el ${fechaCorta(prevista.caduca)}.`}
                </p>
              )}
              {faltaEnvase && (
                <p className="mt-1.5 text-sm font-medium text-amber-900" data-testid="etiqueta-falta-envase">{prevista.texto}</p>
              )}
            </div>
            <div>
              <label htmlFor="etq-lote-fabricante" className="block text-sm font-medium text-gray-700 mb-1">
                Lote del fabricante (opcional)
              </label>
              <Input
                id="etq-lote-fabricante"
                value={loteFabricante}
                onChange={(e) => setLoteFabricante(e.target.value)}
                maxLength={40}
                className="h-11 bg-white border-gray-300 text-base"
                autoComplete="off"
                data-testid="etiqueta-lote-fabricante"
              />
            </div>
            {accion === 'abrir' && (
              <label htmlFor="etq-trasvasado" className="flex items-center justify-between gap-3 min-h-[44px] cursor-pointer">
                <span>
                  <span className="block text-sm font-medium text-gray-900">Lo paso a otro recipiente</span>
                  <span className="block text-xs text-gray-500">Etiqueta completa con alérgenos</span>
                </span>
                <Switch
                  id="etq-trasvasado"
                  checked={trasvasado}
                  onCheckedChange={(v) => setTrasvasado(!!v)}
                  className="data-[state=checked]:bg-duetto-primary data-[state=unchecked]:bg-gray-300 [&>span]:bg-white"
                  data-testid="etiqueta-trasvasado"
                />
              </label>
            )}
          </div>
        )}
      </div>

      <div className={`${claseTarjeta} p-3`}>
        <VistaEtiqueta datos={datosVista} />
      </div>

      {/* Etiquetas abiertas del mismo producto */}
      {unaAbierta && !usarAbierta && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="etiqueta-aviso-abierta">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            {unaCaducada ? (
              <p className="text-sm text-amber-900">
                Hay una abierta desde el {fechaLegible(inicioApertura(unaAbierta))} que caducó el {caducaLegible(unaAbierta)}.
                Retírala antes de abrir otra.
              </p>
            ) : (
              <p className="text-sm text-amber-900">
                Hay una abierta desde el {fechaLegible(inicioApertura(unaAbierta))} (caduca el {caducaLegible(unaAbierta)}).
                ¿Se ha terminado?
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {unaCaducada ? (
              <button
                type="button"
                className={claseBotonPrincipal}
                onClick={() => imprimir({ retirarIds: [unaAbierta.id] })}
                disabled={imprimiendo || bloqueado}
                data-testid="etiqueta-retirar-imprimir"
              >
                {imprimiendo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
                Retirada: imprimir nueva
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className={claseBotonPrincipal}
                  onClick={() => imprimir({ cerrarIds: [unaAbierta.id] })}
                  disabled={imprimiendo || bloqueado}
                  data-testid="etiqueta-terminada-imprimir"
                >
                  {imprimiendo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
                  Sí, terminada: imprimir nueva
                </button>
                <button
                  type="button"
                  className={claseBotonSecundario}
                  onClick={() => setUsarAbierta(true)}
                  disabled={imprimiendo}
                  data-testid="etiqueta-usar-abierta"
                >
                  No, usar la abierta
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {unaAbierta && usarAbierta && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3" data-testid="etiqueta-usa-abierta">
          <p className="text-base font-semibold text-emerald-900">
            Usa la abierta del {fechaLegible(inicioApertura(unaAbierta))}. No hace falta etiqueta nueva.
          </p>
          <p className="text-sm text-emerald-800">
            Lote <span className="font-mono">{unaAbierta.lote}</span> · caduca el {caducaLegible(unaAbierta)}
          </p>
          <button type="button" className={claseBotonPrincipal} onClick={onTerminar}>
            Hecho
          </button>
          <button
            type="button"
            className="w-full h-11 text-sm font-medium text-gray-600 underline underline-offset-2"
            onClick={() => imprimir()}
            disabled={imprimiendo || bloqueado}
          >
            Imprimir una nueva de todas formas
          </button>
        </div>
      )}

      {modoLista && abiertas.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="etiqueta-lista-abiertas">
          <p className="text-sm font-medium text-amber-900">
            {abiertas.length === 1 ? 'Hay 1 abierta.' : `Hay ${abiertas.length} abiertas.`} Marca las que se hayan terminado:
          </p>
          <ul className="space-y-1.5">
            {abiertas.map((a, posicion) => {
              const marcada = marcadas.includes(a.id);
              const caducada = a.estado_caducidad === 'caducado';
              return (
                <li key={a.id}>
                  <label
                    htmlFor={`etq-cerrar-${a.id}`}
                    className="flex items-center gap-3 rounded-lg bg-white border border-amber-100 px-3 py-2 min-h-[48px] cursor-pointer"
                  >
                    <Checkbox
                      id={`etq-cerrar-${a.id}`}
                      checked={marcada}
                      onCheckedChange={(v) => alternarMarcada(a.id, v === true)}
                      className="h-5 w-5 border-gray-400 data-[state=checked]:bg-duetto-primary data-[state=checked]:border-duetto-primary data-[state=checked]:text-white"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-gray-900">
                        Desde el {fechaLegible(inicioApertura(a))} · caduca el {caducaLegible(a)}
                      </span>
                      <span className={`block text-xs ${caducada ? 'text-red-700 font-medium' : 'text-gray-500'}`}>
                        Lote {a.lote}{caducada ? ' · caducada: se retira' : ''}
                      </span>
                    </span>
                    {posicion === 0 && !caducada && (
                      <span className="text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 whitespace-nowrap">
                        Usar primero
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!unaAbierta && (
        <button
          type="button"
          className={claseBotonPrincipal}
          onClick={imprimirConMarcadas}
          disabled={imprimiendo || bloqueado}
          data-testid="etiqueta-imprimir"
        >
          {imprimiendo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
          {modoLista && abiertas.length > 0 ? 'Imprimir nueva' : 'Imprimir etiqueta'}
        </button>
      )}
    </div>
  );
};

// ==================== PESTAÑA ====================

const EtiquetarTab = ({
  productos, gruposIngrediente, gruposPlato, aperturas, cargando, error, onReintentar, onRecargar, soloLectura,
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [tipo, setTipo] = useState(null);
  const [grupo, setGrupo] = useState(null);
  const [claveSel, setClaveSel] = useState(null);

  const etiquetables = useMemo(() => productos.filter((p) => p.se_etiqueta), [productos]);

  const resumenTipo = useMemo(() => {
    const r = { ingrediente: { productos: 0, abiertas: 0 }, plato: { productos: 0, abiertas: 0 } };
    productos.forEach((p) => {
      if (!r[p.tipo]) return;
      r[p.tipo].abiertas += Number(p.abiertas) || 0;
      if (p.se_etiqueta) r[p.tipo].productos += 1;
    });
    return r;
  }, [productos]);

  const gruposDelTipo = useMemo(() => {
    if (!tipo) return [];
    const porGrupo = new Map();
    etiquetables.forEach((p) => {
      if (p.tipo !== tipo) return;
      const g = p.grupo || 'Otros';
      const actual = porGrupo.get(g) || { nombre: g, productos: 0, abiertas: 0 };
      actual.productos += 1;
      actual.abiertas += Number(p.abiertas) || 0;
      porGrupo.set(g, actual);
    });
    const orden = (tipo === 'plato' ? gruposPlato : gruposIngrediente) || [];
    const lista = [];
    orden.forEach((g) => {
      if (porGrupo.has(g)) {
        lista.push(porGrupo.get(g));
        porGrupo.delete(g);
      }
    });
    porGrupo.forEach((g) => lista.push(g));
    return lista;
  }, [tipo, etiquetables, gruposIngrediente, gruposPlato]);

  const productosDelGrupo = useMemo(() => {
    if (!tipo || !grupo) return [];
    return etiquetables
      .filter((p) => p.tipo === tipo && (p.grupo || 'Otros') === grupo)
      .sort(nombreOrden);
  }, [tipo, grupo, etiquetables]);

  const resultados = useMemo(() => {
    if (!busqueda.trim()) return [];
    return productos
      .filter((p) => coincideBusqueda(p.nombre, busqueda))
      .sort((a, b) => (Number(!!b.se_etiqueta) - Number(!!a.se_etiqueta)) || nombreOrden(a, b))
      .slice(0, MAX_RESULTADOS);
  }, [productos, busqueda]);

  const seleccionado = useMemo(
    () => (claveSel ? productos.find((p) => claveProducto(p) === claveSel) || null : null),
    [productos, claveSel],
  );

  const abiertasSeleccionado = useMemo(() => {
    if (!seleccionado) return [];
    return aperturas
      .filter((a) => a.tipo === seleccionado.tipo && a.producto_id === seleccionado.id && (a.estado || 'abierta') === 'abierta')
      .sort((a, b) => String(inicioApertura(a)).localeCompare(String(inicioApertura(b))));
  }, [aperturas, seleccionado]);

  let vista = 'inicio';
  if (seleccionado) vista = 'ficha';
  else if (busqueda.trim()) vista = 'busqueda';
  else if (tipo && grupo) vista = 'productos';
  else if (tipo) vista = 'grupos';

  const atras = useCallback(() => {
    if (claveSel) setClaveSel(null);
    else if (busqueda.trim()) setBusqueda('');
    else if (grupo) setGrupo(null);
    else setTipo(null);
  }, [claveSel, busqueda, grupo]);

  const cambiarBusqueda = useCallback((valor) => {
    setBusqueda(valor);
    setClaveSel(null);
  }, []);

  const terminar = useCallback(() => setClaveSel(null), []);

  const titulo = {
    inicio: '',
    grupos: tipo === 'plato' ? 'Platos' : 'Ingredientes',
    productos: grupo || '',
    busqueda: resultados.length ? `${resultados.length}${resultados.length >= MAX_RESULTADOS ? '+' : ''} resultados` : 'Sin resultados',
    ficha: '',
  }[vista];

  const teselaProducto = (p, conTipo) => (
    <button
      key={claveProducto(p)}
      type="button"
      onClick={() => setClaveSel(claveProducto(p))}
      className={`${claseTesela} ${p.se_etiqueta ? '' : 'opacity-60'}`}
      data-testid="etiqueta-producto"
    >
      <span className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{p.nombre}</span>
      <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
        {conTipo && <span className="text-gray-500">{p.tipo === 'plato' ? 'Plato' : 'Ingrediente'}</span>}
        {textoVida(p) && <span className="text-gray-500">{textoVida(p)}</span>}
        {p.abiertas > 0 && <span className="font-medium text-amber-700">{textoAbiertas(p.abiertas)}</span>}
        {!p.se_etiqueta && <span className="text-gray-500">No se suele etiquetar</span>}
      </span>
    </button>
  );

  return (
    <div className="space-y-3">
      {vista !== 'ficha' && (
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={busqueda}
            onChange={(e) => cambiarBusqueda(e.target.value)}
            placeholder="Buscar ingrediente o plato"
            className="h-12 pl-9 pr-10 bg-white border-gray-300 text-base rounded-xl"
            autoComplete="off"
            data-testid="etiqueta-buscar"
          />
          {busqueda && (
            <button
              type="button"
              onClick={() => cambiarBusqueda('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 inline-flex items-center justify-center text-gray-400 hover:text-gray-600"
              aria-label="Borrar la búsqueda"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {vista !== 'inicio' && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={atras}
            className="h-11 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
            data-testid="etiqueta-atras"
          >
            <ArrowLeft className="w-4 h-4" />
            Atrás
          </button>
          {titulo && <h2 className="text-base font-semibold text-gray-900 truncate">{titulo}</h2>}
        </div>
      )}

      {cargando && !productos.length && (
        <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          Cargando productos...
        </div>
      )}

      {error && !productos.length && !cargando && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 space-y-2">
          <p>{error}</p>
          <button type="button" className={claseBotonSecundario} onClick={onReintentar}>
            <RotateCcw className="w-4 h-4" />
            Reintentar
          </button>
        </div>
      )}

      {vista === 'inicio' && productos.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => { setTipo('ingrediente'); setGrupo(null); }}
            className={`${claseTarjeta} p-4 text-left hover:border-duetto-primary active:bg-gray-50 transition min-h-[132px] flex flex-col`}
            data-testid="etiqueta-tipo-ingrediente"
          >
            <Package className="w-8 h-8 text-duetto-primary" />
            <span className="mt-2 text-lg font-semibold text-gray-900">Ingrediente</span>
            <span className="text-sm text-gray-500 leading-snug">Producto comprado que se abre</span>
            <span className={`mt-auto pt-2 text-xs font-medium ${resumenTipo.ingrediente.abiertas ? 'text-amber-700' : 'text-gray-400'}`}>
              {resumenTipo.ingrediente.abiertas ? textoAbiertas(resumenTipo.ingrediente.abiertas) : 'Ninguno abierto'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => { setTipo('plato'); setGrupo(null); }}
            className={`${claseTarjeta} p-4 text-left hover:border-duetto-primary active:bg-gray-50 transition min-h-[132px] flex flex-col`}
            data-testid="etiqueta-tipo-plato"
          >
            <ChefHat className="w-8 h-8 text-duetto-primary" />
            <span className="mt-2 text-lg font-semibold text-gray-900">Plato</span>
            <span className="text-sm text-gray-500 leading-snug">Elaboración propia</span>
            <span className={`mt-auto pt-2 text-xs font-medium ${resumenTipo.plato.abiertas ? 'text-amber-700' : 'text-gray-400'}`}>
              {resumenTipo.plato.abiertas ? textoAbiertas(resumenTipo.plato.abiertas) : 'Ninguno abierto'}
            </span>
          </button>
        </div>
      )}

      {vista === 'grupos' && (
        gruposDelTipo.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No hay productos para etiquetar.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {gruposDelTipo.map((g) => (
              <button
                key={g.nombre}
                type="button"
                onClick={() => setGrupo(g.nombre)}
                className={claseTesela}
                data-testid="etiqueta-grupo"
              >
                <span className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{g.nombre}</span>
                <span className="mt-1 flex flex-wrap gap-x-2 text-xs">
                  <span className="text-gray-500">{g.productos === 1 ? '1 producto' : `${g.productos} productos`}</span>
                  {g.abiertas > 0 && <span className="font-medium text-amber-700">{textoAbiertas(g.abiertas)}</span>}
                </span>
              </button>
            ))}
          </div>
        )
      )}

      {vista === 'productos' && (
        productosDelGrupo.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No hay productos en este grupo.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {productosDelGrupo.map((p) => teselaProducto(p, false))}
          </div>
        )
      )}

      {vista === 'busqueda' && (
        resultados.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No hay ningún ingrediente ni plato con ese nombre.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {resultados.map((p) => teselaProducto(p, true))}
          </div>
        )
      )}

      {vista === 'ficha' && seleccionado && (
        <FichaProducto
          key={claveSel}
          producto={seleccionado}
          abiertas={abiertasSeleccionado}
          onTerminar={terminar}
          onRecargar={onRecargar}
          soloLectura={soloLectura}
        />
      )}
    </div>
  );
};

export default EtiquetarTab;
